import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { ColDef } from '@siloreal:modules:private/silobag-list/config/extra/types';
import { PLATFORM_GREEN } from './column-field-ui';
import { useState, type MouseEvent } from 'react';
import * as HOOKS from '@siloreal:shared:hooks';

const PLATE_FRAME = '#2a3328';
const PLATE_RIVET = '#8f7d5c';
const COPY_BTN_DIVIDER = '#9aaa82';
const COPY_BTN_TEXT = '#4a5538';

export function buildIdColumn(): ColDef {
  return {
    field: 'id',
    headerName: 'ID',
    minWidth: 148,
    sortable: false,
    headerAlign: 'center',
    renderCell: ({ row }) => <SilobagIdPlate id={row.id} />,
  };
}

function SilobagIdPlate({ id }: { id: string | number }) {
  const [copied, setCopied] = useState(false);
  const idLabel = String(id);

  const handleCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    void navigator.clipboard.writeText(idLabel).then(() => {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    });
  };

  const i18n = HOOKS.useLocale();

  return (
    <Stack height="100%" direction="row" alignItems="center" justifyContent="center">
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          flexDirection: 'column',
          borderRadius: '4px',
          border: `1.5px solid ${PLATE_FRAME}`,
          background: 'linear-gradient(180deg, #f7f5ef 0%, #e8e4d6 55%, #dbd4c4 100%)',
          boxShadow:
            'inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -1px 0 rgba(42,51,40,0.05), 0 1px 2px rgba(42,51,40,0.14)',
          overflow: 'hidden',
          maxWidth: '100%',
        }}
      >
        <PlateRivet sx={{ top: 3, left: 4 }} />
        <PlateRivet sx={{ top: 3, right: 4 }} />

        <Box
          sx={{
            height: 7,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: `linear-gradient(90deg, #5a9900 0%, ${PLATFORM_GREEN} 42%, #7ec816 50%, ${PLATFORM_GREEN} 58%, #5a9900 100%)`,
            borderBottom: `1px solid ${PLATE_FRAME}`,
          }}
        >
          <Typography
            component="span"
            sx={{
              fontSize: 5.5,
              fontWeight: 800,
              letterSpacing: '0.28em',
              color: 'rgba(255,255,255,0.92)',
              lineHeight: 1,
              userSelect: 'none',
            }}
          >
            IDENTIFICADOR
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'stretch', minHeight: 22 }}>
          <Box
            sx={{
              px: 0.9,
              py: 0.4,
              display: 'flex',
              alignItems: 'center',
              flex: 1,
              minWidth: 0,
            }}
          >
            <Typography
              component="span"
              title={idLabel}
              sx={{
                fontFamily: '"Roboto Mono", "SF Mono", monospace',
                fontWeight: 700,
                fontSize: 11,
                letterSpacing: '0.08em',
                color: '#242018',
                lineHeight: 1,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {idLabel}
            </Typography>
          </Box>

          <Tooltip
            title={
              copied ? i18n.t('temp.private.list.common.copied') : i18n.t('temp.private.list.common.copy_id')
            }
            placement="top"
          >
            <Box
              component="button"
              type="button"
              aria-label={copied ? 'ID copiado' : 'Copiar ID'}
              onClick={handleCopy}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 24,
                flexShrink: 0,
                border: 'none',
                borderLeft: `1px solid ${COPY_BTN_DIVIDER}`,
                background: 'linear-gradient(180deg, #e8edd6 0%, #d2dbb8 100%)',
                color: copied ? PLATFORM_GREEN : COPY_BTN_TEXT,
                cursor: 'pointer',
                p: 0,
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.45)',
                transition: 'background 0.15s ease, color 0.15s ease',
                '&:hover': {
                  background: 'linear-gradient(180deg, #eef2e2 0%, #dae3c4 100%)',
                },
                '&:active': {
                  background: 'linear-gradient(180deg, #d0d9bc 0%, #bcc8a4 100%)',
                },
              }}
            >
              {copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 12 }} />}
            </Box>
          </Tooltip>
        </Box>
      </Box>
    </Stack>
  );
}

function PlateRivet({ sx }: { sx: Record<string, string | number> }) {
  return (
    <Box
      sx={{
        position: 'absolute',
        width: 3,
        height: 3,
        borderRadius: '50%',
        bgcolor: PLATE_RIVET,
        boxShadow: 'inset 0 1px 1px rgba(62,48,28,0.28)',
        zIndex: 1,
        ...sx,
      }}
    />
  );
}
